import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { checkSiteDns } from "@/lib/dns-check.functions";
import { requireAdmin } from "@/lib/admin-guard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, RefreshCw, Globe, AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/admin_/dns-check")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "DNS Troubleshooting — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: DnsCheckPage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-destructive">{error instanceof Error ? error.message : "Page unavailable"}</div>
  ),
  notFoundComponent: () => <div className="p-8">Not found.</div>,
});

function StatusBadge({ ok }: { ok: boolean }) {
  return (
    <Badge variant={ok ? "default" : "destructive"} className="gap-1">
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
      {ok ? "Matches" : "Not matching"}
    </Badge>
  );
}

function RecordRow({
  label,
  host,
  type,
  expected,
  actual,
  ok,
  error,
  extra,
}: {
  label: string;
  host: string;
  type: string;
  expected: string;
  actual: string[];
  ok: boolean;
  error?: string;
  extra?: { label: string; values: string[] } | null;
}) {
  return (
    <div className="rounded-md border p-4 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium">{label}</div>
          <div className="text-xs text-muted-foreground font-mono truncate">
            {type} {host}
          </div>
        </div>
        <StatusBadge ok={ok} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
            Expected
          </div>
          <div className="font-mono text-xs bg-muted rounded px-2 py-1">{expected}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
            Found
          </div>
          {error ? (
            <div className="text-xs text-destructive">Lookup error: {error}</div>
          ) : actual.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">No records</div>
          ) : (
            <div className="space-y-1">
              {actual.map((v, i) => (
                <div
                  key={i}
                  className="font-mono text-xs bg-muted rounded px-2 py-1 break-all"
                >
                  {v}
                </div>
              ))}
            </div>
          )}
          {extra && extra.values.length > 0 && (
            <div className="mt-2 text-xs text-muted-foreground">
              {extra.label}:{" "}
              <span className="font-mono">{extra.values.join(", ")}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DnsCheckPage() {
  const router = useRouter();
  const fetcher = useServerFn(checkSiteDns);
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["dns-check"],
    queryFn: () => fetcher({}),
    refetchInterval: 60_000,
  });

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <button
              onClick={() => router.navigate({ to: "/admin" })}
              className="hover:underline"
            >
              Admin
            </button>
            <span>/</span>
            <span>DNS Troubleshooting</span>
          </div>
          <h1 className="text-3xl font-semibold flex items-center gap-2 mt-1">
            <Globe className="h-7 w-7 text-primary" />
            DNS Troubleshooting
          </h1>
          <p className="text-muted-foreground mt-1">
            Live DNS lookup for <span className="font-mono">recoverytrauma.com</span> via
            Cloudflare public resolver. Auto-refreshes every 60s.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`h-4 w-4 me-2 ${isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Required records</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            Add these at your DNS provider (Cloudflare). If A records are proxied
            (orange cloud), switch them to <strong>DNS only</strong> (grey cloud) or
            re-add the domain in Lovable with the Cloudflare proxy option ticked.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border rounded-md">
              <thead className="bg-muted">
                <tr className="text-start">
                  <th className="p-2 font-medium">Type</th>
                  <th className="p-2 font-medium">Name</th>
                  <th className="p-2 font-medium">Value</th>
                </tr>
              </thead>
              <tbody className="font-mono text-xs">
                <tr className="border-t">
                  <td className="p-2">A</td>
                  <td className="p-2">@</td>
                  <td className="p-2">185.158.133.1</td>
                </tr>
                <tr className="border-t">
                  <td className="p-2">A</td>
                  <td className="p-2">www</td>
                  <td className="p-2">185.158.133.1</td>
                </tr>
                <tr className="border-t">
                  <td className="p-2">TXT</td>
                  <td className="p-2">_lovable</td>
                  <td className="p-2">lovable_verify=… (from Lovable setup dialog)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-destructive">
          <CardContent className="p-4 text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5" />
            {(error as Error).message}
          </CardContent>
        </Card>
      )}

      {isLoading && !data && (
        <Card>
          <CardContent className="p-6 text-muted-foreground">Looking up DNS…</CardContent>
        </Card>
      )}

      {data && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Current lookup</CardTitle>
            <Badge variant={data.allOk ? "default" : "destructive"} className="gap-1">
              {data.allOk ? (
                <CheckCircle2 className="h-3 w-3" />
              ) : (
                <XCircle className="h-3 w-3" />
              )}
              {data.allOk ? "All records match" : "Records need attention"}
            </Badge>
          </CardHeader>
          <CardContent className="space-y-3">
            <RecordRow
              label="Root domain"
              host={data.results.root.host}
              type="A"
              expected={data.results.root.expected}
              actual={data.results.root.records}
              ok={data.results.root.ok}
              error={"error" in data.results.root ? data.results.root.error : undefined}
            />
            <RecordRow
              label="www subdomain"
              host={data.results.www.host}
              type="A"
              expected={data.results.www.expected}
              actual={data.results.www.records}
              ok={data.results.www.ok}
              error={"error" in data.results.www ? data.results.www.error : undefined}
              extra={
                data.results.www.cname && data.results.www.cname.length > 0
                  ? { label: "CNAME target", values: data.results.www.cname }
                  : null
              }
            />
            <RecordRow
              label="Lovable verification"
              host={data.results.txt.host}
              type="TXT"
              expected="lovable_verify=…"
              actual={data.results.txt.records}
              ok={data.results.txt.ok}
              error={"error" in data.results.txt ? data.results.txt.error : undefined}
            />
            <div className="text-xs text-muted-foreground pt-2">
              Checked at {new Date(data.checkedAt).toLocaleString()} · resolver:
              cloudflare-dns.com
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">If records look correct but Lovable still fails</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <ul className="list-disc list-inside space-y-1">
            <li>Wait for propagation (usually minutes on Cloudflare, up to 72h elsewhere).</li>
            <li>Remove any stale A records for @ or www pointing elsewhere.</li>
            <li>Turn off the Cloudflare proxy (orange cloud) on both A records, or re-add the domain with the proxy option ticked to switch to CNAME verification.</li>
            <li>
              Cross-check externally at{" "}
              <a
                href="https://dnschecker.org/#A/recoverytrauma.com"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                dnschecker.org
              </a>
              .
            </li>
            <li>Then hit Verify in Project Settings → Domains.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
